import React, { useEffect, useLayoutEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RiMenuLine } from "react-icons/ri";
import { LuSearch, LuSquarePen, LuAudioLines, LuImage, LuArrowUp, LuArrowDown, LuRefreshCw, LuUsers, LuLogOut } from "react-icons/lu";
import { IoMdStar } from "react-icons/io";
import { FaUserCircle } from "react-icons/fa";
import { ConversationsContext } from "../contexts/ConversationsContext";
import { motion, AnimatePresence } from "framer-motion";
import Modal from "./Modal";
import { useToast } from "../contexts/ToastContext";
import SearchModal from "./SearchModal";
import logo from "../resources/logo.png";
import "../styles/Sidebar.css";

const itemVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, x: 40 }
};

const itemContentVariants = {
  exit: { height: 0 }
};

const RenameInput = ({ initialValue, conversationId, handleRename, setRenamingConversationId }) => {
  const [value, setValue] = useState(initialValue);
  return (
    <input
      type="text"
      className="rename-input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleRename(conversationId, value);
        } else if (e.key === "Escape") {
          setRenamingConversationId(null);
        }
      }}
      enterKeyHint="done"
      onBlur={() => {
        if (value.trim()) {
          handleRename(conversationId, value);
        } else {
          setRenamingConversationId(null);
        }
      }}
      autoFocus
    />
  );
};

const ConversationItem = React.memo(({
  conv,
  isActive,
  isSelected,
  isRenaming,
  handleRename,
  setRenamingConversationId,
  handleNavigate,
  handleConversationContextMenu,
  handleTouchStart,
  handleTouchEnd,
  handleTouchMove,
  isTouch
}) => {
  return (
    <motion.li
      layout
      variants={itemVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.2, ease: "easeInOut" }}
      onContextMenu={(e) => {
        e.preventDefault();
        handleConversationContextMenu(e, conv.conversation_id)
      }}
      onTouchStart={(e) => handleTouchStart(e, conv.conversation_id)}
      onTouchEnd={(e) => handleTouchEnd(e, conv.conversation_id)}
      onTouchMove={handleTouchMove}
      onTouchCancel={(e) => handleTouchEnd(e, conv.conversation_id)}
    >
      <motion.div
        variants={itemContentVariants}
        style={{ overflow: 'hidden' }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
      <div
        className={`conversation-item ${isActive ? "active-conversation" : ""} ${isSelected ? "selected" : ""}`}
        onClick={!isTouch ? () => {
          if (!isRenaming) {
            handleNavigate(conv.conversation_id);
          }
        } : undefined}
      >
        {isRenaming ? (
          <RenameInput
            initialValue={conv.alias}
            conversationId={conv.conversation_id}
            handleRename={handleRename}
            setRenamingConversationId={setRenamingConversationId}
          />
        ) : (
          <>
            {conv.isLoading ? (
              <span key="loading" className="loading-text">
                로딩 중...
              </span>
            ) : (
              <span key="alias" className="conversation-text">
                {conv.alias}
              </span>
            )}
          </>
        )}
        {conv.starred && (
          <span className="star-icon">
            <IoMdStar />
          </span>
        )}
      </div>
      </motion.div>
    </motion.li>
  );
});

function Sidebar({
  isSidebarOpen,
  toggleSidebar,
  isResponsive,
  isTouch,
  userInfo,
  setAlias,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdown, setIsDropdown] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [renamingConversationId, setRenamingConversationId] = useState(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const { showToast } = useToast();
  const [isActiveVisible, setIsActiveVisible] = useState(true);
  const [activeDirection, setActiveDirection] = useState('down');
  
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
  });
  const containerRef = useRef(null);
  const userContainerRef = useRef(null);
  const longPressTimer = useRef(null);
  const contextMenuProtected = useRef(false);
  const contextMenuVisibleRef = useRef(false);
  const renamingIdRef = useRef(null);
  const conversationsRef = useRef([]);

  useLayoutEffect(() => {
    contextMenuVisibleRef.current = contextMenu.visible;
  }, [contextMenu.visible]);

  const {
    conversations,
    deleteConversation,
    updateAlias,
    toggleStarConversation
  } = useContext(ConversationsContext);

  useLayoutEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const sortedConversations = useMemo(() => {
    return [...conversations].sort((a, b) => {
      if (a.starred && !b.starred) return -1;
      if (!a.starred && b.starred) return 1;
      
      if (a.starred && b.starred) {
        if (!a.starred_at) return 1;
        if (!b.starred_at) return -1;
        return new Date(b.starred_at) - new Date(a.starred_at);
      }
      
      const timeA = a.updated_at ? new Date(a.updated_at) : new Date(a.created_at);
      const timeB = b.updated_at ? new Date(b.updated_at) : new Date(b.created_at);
      return timeB - timeA;
    });
  }, [conversations]);

  const handleNavigate = useCallback((conversation_id) => {
    const conv = conversationsRef.current.find(
      (c) => c.conversation_id === conversation_id
    );
    if (!conv) {
      showToast("대화가 존재하지 않습니다.");
      return;
    }
    const targetPath = conv.type === 'image' ? `/image/${conversation_id}` : `/chat/${conversation_id}`;
    navigate(targetPath);
    if (isResponsive) toggleSidebar();
  }, [navigate, isResponsive, toggleSidebar, showToast]);

  const toggleStar = useCallback(async (conversation_id, e) => {
    e.stopPropagation();
    try {
      const conversation = conversations.find(c => c.conversation_id === conversation_id);
      if (!conversation) return;

      toggleStarConversation(conversation_id, !conversation.starred);
      
      const res = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/conversation/${conversation_id}/star`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ starred: !conversation.starred })
      });
      if (res.status === 401 && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
          window.location.href = '/login?expired=true';
      }
      if (!res.ok) {
        throw new Error('즐겨찾기 토글이 실패했습니다.');
      }
    } catch (error) {
      showToast("즐겨찾기 토글이 실패했습니다.");
      const conversation = conversations.find(c => c.conversation_id === conversation_id);
      if (conversation) {
        toggleStarConversation(conversation_id, conversation.starred);
      }
    }
  }, [conversations, toggleStarConversation, showToast]);

  const handleTouchStart = useCallback((e, conversation_id) => {
    setContextMenu(prev => ({ ...prev, visible: false }));
    
    longPressTimer.current = setTimeout(() => {
      setSelectedConversationId(conversation_id);
      setContextMenu({
        visible: true,
        x: e.touches[0].pageX,
        y: e.touches[0].pageY,
      });
      
      if (navigator.vibrate) {
        navigator.vibrate(100);
      }
      
      contextMenuProtected.current = true;
      setTimeout(() => {
        contextMenuProtected.current = false;
      }, 500);
      
      const preventDefaultOnce = (evt) => {
        evt.preventDefault();
        document.removeEventListener('contextmenu', preventDefaultOnce);
      };
      document.addEventListener('contextmenu', preventDefaultOnce);
    }, 500);
  }, []);

  const handleTouchEnd = useCallback((e, conversation_id) => {
    if (contextMenuVisibleRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;

      if (renamingIdRef.current !== conversation_id) {
        handleNavigate(conversation_id);
      }
    }
  }, [handleNavigate]);

  const handleTouchMove = useCallback((e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const match = location.pathname.match(/^\/(?:chat|image)\/([^/]+)/);
  const currentConversationId = match ? match[1] : null;

  const currentConversationIdRef = useRef(currentConversationId);
  useLayoutEffect(() => {
    currentConversationIdRef.current = currentConversationId;
    renamingIdRef.current = renamingConversationId;
  }, [currentConversationId, renamingConversationId]);

  const handleRename = useCallback(async (conversation_id, newAlias) => {
    try {
      updateAlias(conversation_id, newAlias);
      setRenamingConversationId(null);

      if (conversation_id === currentConversationIdRef.current) {
        setAlias(newAlias);
      }
  
      const res = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/conversation/${conversation_id}/rename`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias: newAlias })
      });
      if (res.status === 401 && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login?expired=true';
      }
      if (!res.ok) {
        throw new Error('대화 이름 편집에 실패했습니다.');
      }
    } catch (error) {
      console.error("Failed to rename conversation.", error);
      showToast("대화 이름 편집에 실패했습니다.");
    }
  }, [updateAlias, setAlias, showToast]);

  const handleDelete = useCallback(async (conversation_id) => {
    try {
      deleteConversation(conversation_id);
      if (currentConversationId === conversation_id)
        navigate("/");

      const res = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/conversation/${conversation_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.status === 401 && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login?expired=true';
      }
      if (!res.ok) {
        throw new Error('대화 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error("Failed to delete conversation.", error);
      showToast("대화 삭제에 실패했습니다.");
    }
  }, [deleteConversation, currentConversationId, navigate, showToast]);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const handleAdminClick = useCallback(() => {
    navigate("/admin");
    setIsDropdown(false);
    if (isResponsive) toggleSidebar();
  }, [navigate, isResponsive, toggleSidebar]);

  const handleLogoutClick = useCallback(() => {
    setModalMessage("정말 로그아웃 하시겠습니까?");
    setModalAction("logout");
    setShowModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (modalAction === "logout") {
      try {
        {
          const res = await fetch(`${process.env.REACT_APP_FASTAPI_URL}/logout`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({})
          });
          if (res.status === 401 && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
            window.location.href = '/login?expired=true';
          }
          if (!res.ok) {
            let detail = null;
            try { detail = (await res.json())?.detail; } catch {}
            throw new Error(detail || '알 수 없는 오류가 발생했습니다.');
          }
        }
        window.location.href = '/login';
      } catch (error) {
        const detail = error.response?.data?.detail;
        showToast(
          !Array.isArray(detail) && detail
            ? detail
            : "알 수 없는 오류가 발생했습니다."
        );
      }
    }
    setShowModal(false);
    setModalAction(null);
  }, [modalAction, showToast]);

  const cancelDelete = useCallback(() => {
    setShowModal(false);
    setModalAction(null);
  }, []);

  const handleScrollToActive = useCallback(() => {
    const activeEl = containerRef.current?.querySelector('.active-conversation');
    activeEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleNewConversation = useCallback(() => {
    navigate("/");
    if (isResponsive) toggleSidebar();
  }, [navigate, isResponsive, toggleSidebar]);

  const handleRealtimeConversation = useCallback(() => {
    navigate("/realtime");
    if (isResponsive) toggleSidebar();
  }, [navigate, isResponsive, toggleSidebar]);

  const handleImageGeneration = useCallback(() => {
    navigate("/image");
    if (isResponsive) toggleSidebar();
  }, [navigate, isResponsive, toggleSidebar]);

  const handleConversationContextMenu = useCallback((e, conversation_id) => {
    e.preventDefault();
    if (renamingIdRef.current !== null) return;

    setSelectedConversationId(conversation_id);
    setContextMenu({
      visible: true,
      x: e.pageX,
      y: e.pageY,
    });
  }, []);
  
  useEffect(() => {
    const handleClickOutsideContextMenu = () => {
      if (contextMenuVisibleRef.current && !contextMenuProtected.current) {
        setContextMenu(prev => ({ ...prev, visible: false }));
        setSelectedConversationId(null);
      }
    };
    document.addEventListener("click", handleClickOutsideContextMenu);
    return () =>
      document.removeEventListener("click", handleClickOutsideContextMenu);
  }, []);

  useEffect(() => {
    const handleClickOutsideDropdown = (e) => {
      if (
        userContainerRef.current &&
        !userContainerRef.current.contains(e.target)
      ) {
        setIsDropdown(false);
      }
    };
    if (isDropdown) {
      document.addEventListener("click", handleClickOutsideDropdown);
    }
    return () => {
      document.removeEventListener("click", handleClickOutsideDropdown);
    };
  }, [isDropdown]);

  useLayoutEffect(() => {
    if (!isSidebarOpen) setIsSearchVisible(false);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!currentConversationId || !containerRef.current) {
      setIsActiveVisible(true);
      return;
    }
    const container = containerRef.current;
    const activeEl = container.querySelector('.active-conversation');
    if (!activeEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const scrollable = container.scrollHeight > container.clientHeight + 1;
        setIsActiveVisible(!scrollable || entry.isIntersecting);
        if (scrollable && !entry.isIntersecting) {
          const rect = entry.boundingClientRect;
          const containerRect = container.getBoundingClientRect();
          setActiveDirection(rect.top < containerRect.top ? 'up' : 'down');
        }
      },
      { root: container, threshold: 0.5 }
    );
    observer.observe(activeEl);
    return () => observer.disconnect();
  }, [currentConversationId, sortedConversations]);

  const handleCustomAction = useCallback((action) => {
    if (action === "star") {
      if (selectedConversationId) {
        const conv = conversations.find(
          (c) => c.conversation_id === selectedConversationId
        );
        if (conv) {
          toggleStar(conv.conversation_id, { stopPropagation: () => {} });
        }
      }
    } else if (action === "rename") {
      if (selectedConversationId) {
        setRenamingConversationId(selectedConversationId);
      }
    } else if (action === "delete") {
      if (selectedConversationId) {
        handleDelete(selectedConversationId);
      }
    }
    setContextMenu(prev => ({ ...prev, visible: false }));
    setSelectedConversationId(null);
  }, [selectedConversationId, conversations, toggleStar, handleDelete]);

  const toggleSearch = useCallback(() => {
    setIsSearchVisible(prev => !prev);
  }, []);

  return (
    <>
      <div className="sidebar">
        <div className="header sidebar-header">
          <div className="header-left">
            <div className="logo">
              <img src={logo} alt="DEVOCHAT" className="logo-image" />
            </div>
          </div>
          <div className="header-right">
            <div className="header-icon">
              <RiMenuLine onClick={toggleSidebar} />
            </div>
          </div>
        </div>

        <div className="newtask-container">
          <div className="new-task search" onClick={toggleSearch}>
            <LuSearch />
            검색
          </div>
          <div className="new-task" onClick={handleNewConversation}>
            <LuSquarePen />
            새 대화
          </div>
          <div className="new-task" onClick={handleImageGeneration}>
            <LuImage />
            이미지 생성
          </div>
          <div className="new-task" onClick={handleRealtimeConversation}>
            <LuAudioLines />
            음성 대화
          </div>
        </div>

        <div className="conversation-list-wrapper">
        <div className="conversation-container" ref={containerRef}>
          <div className="conversation-header">
            대화 기록
          </div>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {sortedConversations.length > 0 ? (
              <AnimatePresence initial={false}>
                {sortedConversations.map((conv) => (
                  <ConversationItem
                    key={conv.conversation_id}
                    conv={conv}
                    isActive={currentConversationId === conv.conversation_id}
                    isSelected={selectedConversationId === conv.conversation_id}
                    isRenaming={renamingConversationId === conv.conversation_id}
                    handleRename={handleRename}
                    setRenamingConversationId={setRenamingConversationId}
                    handleNavigate={handleNavigate}
                    handleConversationContextMenu={handleConversationContextMenu}
                    handleTouchStart={handleTouchStart}
                    handleTouchEnd={handleTouchEnd}
                    handleTouchMove={handleTouchMove}
                    isTouch={isTouch}
                  />
                ))}
              </AnimatePresence>
            ) : (
              <div className="no-result">
                {conversations.length === 0 ? "대화 내역이 없습니다." : "검색 결과가 없습니다."}
              </div>
            )}
          </div>
        </div>
          {currentConversationId && (
            <button
              className={`scroll-to-active-btn ${!isActiveVisible ? 'visible' : ''}`}
              onClick={handleScrollToActive}
            >
              {activeDirection === 'up' ? <LuArrowUp /> : <LuArrowDown />}
            </button>
          )}
        </div>

        <div className="user-container" ref={userContainerRef}>
          <div className={`user-info${isDropdown ? ' active' : ''}`} onClick={() => setIsDropdown(!isDropdown)}>
            <FaUserCircle className="user-icon" />
            <div className="user-text">
              <div className="user-name">{userInfo?.name}</div>
              <div className="user-billing-inline">{userInfo?.billing?.toFixed(2)}$ 사용됨</div>
            </div>
          </div>

          <AnimatePresence>
            {isDropdown && (
              <motion.div
                className="user-dropdown"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="dropdown-menu">
                  <div onClick={handleRefresh} className="dropdown-item">
                    <LuRefreshCw className="dropdown-item-icon" />
                    페이지 새로고침
                  </div>
                  {userInfo?.admin && (
                    <div onClick={handleAdminClick} className="dropdown-item">
                      <LuUsers className="dropdown-item-icon" />
                      사용자 관리
                    </div>
                  )}
                  <div onClick={handleLogoutClick} className="dropdown-item logout">
                    <LuLogOut className="dropdown-item-icon" />
                    로그아웃
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <SearchModal
        isVisible={isSearchVisible}
        onClose={toggleSearch}
        sortedConversations={sortedConversations}
        onSelectConversation={handleNavigate}
      />

      <div
        className={`context-menu ${contextMenu.visible ? "visible" : ""}`}
        style={{
          position: "absolute",
          top: contextMenu.y,
          left: contextMenu.x,
        }}
      >
        <ul>
          {selectedConversationId && (
            <>
              {conversations.find(c => c.conversation_id === selectedConversationId)?.starred ? (
                <li onClick={() => handleCustomAction("star")}>즐겨찾기 해제</li>
              ) : (
                <li onClick={() => handleCustomAction("star")}>즐겨찾기</li>
              )}
              <li onClick={() => handleCustomAction("rename")}>이름 편집</li>
              <li onClick={() => handleCustomAction("delete")}>삭제</li>
            </>
          )}
        </ul>
      </div>

      <AnimatePresence>
        {showModal && (
          <Modal
            message={modalMessage}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
            showCancelButton={modalAction !== "notify"}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default React.memo(Sidebar);

